import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Save, Send, X, FilePlus2, Copy } from "lucide-react";
import { toast } from "sonner";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export function ModelForm({ product, editing, onSaved, onNew }) {
  const [name, setName] = useState(editing?.name || "");
  const [sel, setSel] = useState(editing?.selections || {});
  const [saving, setSaving] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const locked = !!editing && editing.status !== "Draft";

  const persist = async () => {
    const body = { name, selections: sel };
    const { data } = editing
      ? await api.patch(`/inst/my/models/${editing.id}`, body)
      : await api.post("/inst/my/models", { ...body, product_id: product.id });
    return data.model;
  };
  const save = async () => {
    setSaving(true);
    try { const m = await persist(); toast.success("Model saved to My Models"); onSaved(m); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const missing = product.fields.filter((f) => !sel[f.label]).map((f) => f.label);
  const startQuote = () => {
    if (missing.length) { toast.error(`Please choose: ${missing.join(", ")}`); return; }
    setQuoteOpen(true);
  };

  return (
    <section className="mt-12 bg-white border border-zinc-200 p-5 sm:p-8" data-testid="inst-model-form">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-[#0F172A]">Request Your Express Quote Now!</h2>
          <p className="text-sm text-zinc-500 mt-1">{locked ? `"${editing.name}" was sent for a quote (${editing.status}). Duplicate it from My Models to make changes.` : editing ? `Editing "${editing.name}"` : "Configure a new model, save it, and request a quote."}</p>
        </div>
        {editing && <button onClick={onNew} data-testid="inst-model-new" className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#0F172A]"><FilePlus2 className="h-4 w-4" /> Start a new model</button>}
      </div>
      <label className="block mt-6 text-sm font-medium text-[#0F172A] max-w-md">Model name
        <input disabled={locked} value={name} onChange={(e) => setName(e.target.value)} placeholder={`e.g. Plant 2 – Line A ${product.code || ""}`} data-testid="inst-model-name"
          className="mt-1 w-full px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
      </label>
      <ModelNumber product={product} sel={sel} />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-4">
        {product.fields.map((f, i) => (
          <label key={f.label} className="block text-sm font-medium text-[#0F172A]"><span className="font-mono text-[10px] text-zinc-400 mr-1">{i + 1}.</span>{f.label}
            <select disabled={locked} value={sel[f.label] || ""} onChange={(e) => setSel({ ...sel, [f.label]: e.target.value })} data-testid={`inst-field-select-${slug(f.label)}`}
              className={`mt-1 w-full px-3 py-2.5 border bg-white text-sm font-normal outline-none focus:ring-2 focus:ring-[#D4AF37] ${sel[f.label] ? "border-zinc-300" : "border-zinc-300 text-zinc-400"}`}>
              <option value="">Select…</option>
              {f.options.map((o) => <option key={o.label} value={o.label}>{o.label}{o.code ? ` (${o.code})` : ""}</option>)}
            </select>
          </label>
        ))}
      </div>
      {!locked && <div className="mt-8 flex flex-wrap justify-center gap-3">
        <button onClick={save} disabled={saving} data-testid="inst-model-save" className="flex items-center gap-2 border border-[#0F172A] text-[#0F172A] px-6 py-3 text-sm font-semibold hover:bg-[#0F172A] hover:text-white transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save model
        </button>
        <button onClick={startQuote} data-testid="inst-model-request-quote" className="flex items-center gap-2 bg-[#D4AF37] text-[#0F172A] px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-[#C9A42F] transition-colors">
          <Send className="h-4 w-4" /> Request Express Quote
        </button>
      </div>}
      {quoteOpen && <QuoteModal persist={persist} onClose={() => setQuoteOpen(false)} onDone={(m) => { setQuoteOpen(false); onSaved(m); }} />}
    </section>
  );
}

export function modelNumberParts(product, sel) {
  return product.fields.map((f) => {
    const o = f.options.find((x) => x.label === sel[f.label]);
    return { field: f.label, code: o ? (o.code || "?") : null };
  });
}

function ModelNumber({ product, sel }) {
  const parts = modelNumberParts(product, sel);
  const sep = product.code_separator ?? "-";
  const full = [product.code || product.name, ...parts.map((p) => p.code || "?")].join(sep);
  return (
    <div className="mt-6 bg-[#0F172A] text-white p-4 sm:p-5" data-testid="inst-model-number-bar">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Custom Model Number</p>
        <button type="button" onClick={() => { navigator.clipboard?.writeText(full); toast.success("Model number copied"); }} data-testid="inst-model-number-copy" className="text-zinc-400 hover:text-white"><Copy className="h-4 w-4" /></button>
      </div>
      <div className="mt-2 font-mono text-lg sm:text-2xl font-bold flex flex-wrap items-center break-all" data-testid="inst-model-number">
        <span>{product.code || product.name}</span>
        {parts.map((p) => (
          <span key={p.field} className="flex items-center" title={p.field}>
            <span className="text-zinc-500">{sep}</span>
            <span className={p.code ? "text-[#D4AF37]" : "text-zinc-600"}>{p.code || "?".repeat(1)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function QuoteModal({ persist, onClose, onDone }) {
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const m = await persist();
      const { data } = await api.post(`/inst/my/models/${m.id}/request-quote`, { quantity: Number(qty) || 1, notes });
      toast.success("Quote request sent to Peregrine");
      onDone(data.model);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div onClick={onClose} className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="inst-quote-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md">
        <div className="flex items-center justify-between p-5 bg-[#0F172A] text-white">
          <h3 className="font-display font-bold">Request Express Quote</h3>
          <button type="button" onClick={onClose} data-testid="inst-quote-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block text-sm font-medium text-[#0F172A]">Quantity
            <input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="inst-quote-qty" className="mt-1 w-full px-3 py-2.5 border border-zinc-300 text-sm" />
          </label>
          <label className="block text-sm font-medium text-[#0F172A]">Notes for Peregrine (optional)
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="inst-quote-notes" className="mt-1 w-full px-3 py-2.5 border border-zinc-300 text-sm" />
          </label>
          <p className="text-xs text-zinc-500">Once sent, this model is locked. You can duplicate it later to make changes.</p>
          <button type="submit" disabled={busy} data-testid="inst-quote-submit" className="w-full flex justify-center bg-[#D4AF37] text-[#0F172A] py-3 text-sm font-bold uppercase tracking-wide disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send request"}
          </button>
        </div>
      </form>
    </div>
  );
}
